import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IInfiniteGetNodeContentQueryData } from '@/api/nodes/queries/get-node-content-query'
import { updateInfiniteNodesOptimistically } from '@/api/nodes/helpers/update-infinite-nodes-optimistically'
import { cleanNodePath } from '@/features/drive/entities/directory/directory-context'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { QueryKey } from '@tanstack/react-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

interface IMoveDirectoryContentParams {
    organizationId: string
    brandSlug: string

    newDirectoryPath: string
    selectedCount: number
    selectedDirectoryPath: string
}

export type IMoveDirectoryContentPayload = Omit<IMoveDirectoryContentParams, 'organizationId' | 'brandSlug'>

type IMoveDirectoryContentApiPayload = {
    parentPath: string
    newDirectoryPath: string
}

export class MoveDirectoryContentMutation {
    static call(api: IApi, { organizationId, brandSlug, ...payload }: IMoveDirectoryContentParams) {
        return api.post<INode, IMoveDirectoryContentApiPayload>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/node/move-content`,
            {
                parentPath: payload.selectedDirectoryPath,
                newDirectoryPath: payload.newDirectoryPath,
            }
        )
    }
}

interface IUseMoveDirectoryContentMutationParams {
    nodeContentQueryKey: QueryKey
    sendToastSuccess: (count: number) => unknown
    sendToastError: (count: number) => unknown
}

export const useMoveDirectoryContentMutation = ({
    nodeContentQueryKey,
    sendToastError,
    sendToastSuccess,
}: IUseMoveDirectoryContentMutationParams) => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (payload: IMoveDirectoryContentPayload) => {
            const promise = MoveDirectoryContentMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            })

            let prev: IInfiniteGetNodeContentQueryData | undefined

            updateInfiniteNodesOptimistically({
                qc,
                nodeContentQueryKey,
                onSuccess: sendToastSuccess,
                allFromDirectoryPath: payload.selectedDirectoryPath,
                selectedCount: payload.selectedCount,

                reducePage: (acc, node, _i, _a, { nodesCount, meta }) => {
                    const isMovedNode = meta
                        ? cleanNodePath(meta.path) === cleanNodePath(payload.selectedDirectoryPath)
                        : false

                    if (isMovedNode) return acc

                    const isReceivingNode = payload.newDirectoryPath === node.path && node.type === 'DIRECTORY'

                    if (isReceivingNode) {
                        return [
                            ...acc,
                            { ...node, numberOfItems: (node.numberOfItems ?? 0) + nodesCount },
                        ] satisfies INode[]
                    }

                    return [...acc, node]
                },

                assignPrev: (prevData) => {
                    prev = prevData
                },
            })

            return promise.then((res) => {
                // Invalidation promise must be returned
                return qc
                    .invalidateQueries({
                        predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                    })
                    .catch((e) => {
                        sendToastError(payload.selectedCount)

                        qc.setQueryData<IInfiniteGetNodeContentQueryData>(nodeContentQueryKey, prev)
                        qc.refetchQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })

                        throw new Error(e)
                    })
                    .finally(() => {
                        return res
                    })
            })
        },
    })
}
